import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function QuickViewModal({ product, open, onClose }) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  if (!product) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="quickview-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 relative max-h-[92vh] overflow-y-auto"
            data-testid="quickview-modal"
          >
            <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 bg-white/90 flex items-center justify-center" data-testid="quickview-close">
              <X size={18} />
            </button>
            <div className="bg-[#F8F5F0] aspect-square md:aspect-auto">
              <img src={product.image} alt={product.name} decoding="async" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-10 flex flex-col">
              <div className="overline mb-3">{product.category}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">{product.name}</h2>
              <div className="flex items-center gap-1 mt-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={13} fill="#C9A961" strokeWidth={0} color="#C9A961" />
                ))}
                <span className="text-xs text-[#5C5C5C] ml-2">{product.review_count} reviews</span>
              </div>
              <div className="flex items-baseline gap-3 mt-5">
                <span className="font-serif-display text-2xl">{formatINR(product.price)}</span>
                {product.original_price > product.price && (
                  <span className="text-[#5C5C5C] line-through">{formatINR(product.original_price)}</span>
                )}
              </div>
              <p className="text-sm text-[#3a3a3a] leading-relaxed mt-5 whitespace-pre-line">{product.description}</p>

              <div className="flex items-center gap-2 mt-6 flex-wrap">
                {product.badges?.map((b) => (
                  <span key={b} className="text-[10px] tracking-[0.22em] uppercase border border-[#C9A961] text-[#9d8345] px-3 py-1">
                    {b}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 mt-8">
                <div className="flex items-center border border-black/15">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-12 hover:bg-[#F8F5F0]" data-testid="quickview-qty-minus">−</button>
                  <span className="w-10 text-center" data-testid="quickview-qty">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="w-10 h-12 hover:bg-[#F8F5F0]" data-testid="quickview-qty-plus">+</button>
                </div>
                <button
                  onClick={() => {
                    addItem(product, qty);
                    onClose();
                  }}
                  className="btn-primary flex-1"
                  data-testid="quickview-add-to-cart"
                >
                  <ShoppingBag size={14} /> Add to Bag
                </button>
              </div>

              <Link to={`/product/${product.id}`} onClick={onClose} className="overline text-center mt-5 hover:text-[#C9A961]" data-testid="quickview-details-link">
                View full details →
              </Link>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
